import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { take } from 'rxjs/operators';

import { IMultiSelectOption, IMultiSelectSettings, IMultiSelectTexts } from 'ngx-bootstrap-multiselect';
import { ApiError, MessageService } from '../../../../../core';
import { CampanhaSearchOrderBy } from '../../campanha-search-orderby';
import { CampanhaFilter } from '../../filter/campanha-filter';
import { CampanhaSearchHttpService } from '../../services';
import { campanhaStatusEmAndamento } from '../../status';
import { AreaCampanhaSelect, createAreasCampanhasSelect } from '../select';

@Component({
    selector: 'sgt-campanha-multiselect',
    templateUrl: './campanha-multiselect.component.html',
    styleUrls: ['./campanha-multiselect.component.less']
})
export class CampanhaMultiselectComponent implements OnInit, OnDestroy {
    /** Campanhas selecionadas */
    @Input() idsCampanhas: number[];
    /** Ao alterar a seleção das campanhas */
    @Output() idsCampanhasChange = new EventEmitter<number[]>();

    /** Se deve exibir a opção de selecionar a opção de campanha nula ou seja, para filtrar registros que não possuem a campanha */
    @Input() showNullOption = false;
    /** Valor da opção de selecionar a campanha nula */
    @Input() nullOption = false;
    /** Ao alterar a seleção da opção da campanha nula */
    @Output() nullOptionChange = new EventEmitter<boolean>();

    /** Input Name */
    @Input() nameSomenteEmAndamento = 'multiselectCampanhaSomenteEmAndamento';
    /** Input ID */
    @Input() idSomenteEmAndamento = 'multiselectCampanhaSomenteEmAndamento';
    /** Se deve filtrar somente as campanhas em andamento */
    @Input() somenteEmAndamento = true;
    @Output() somenteEmAndamentoChange = new EventEmitter<boolean>();

    /** Se deve filtrar somente as campanhas que estão em uso */
    @Input() somenteEmUso = false;
    /** Objetivo de venda */
    @Input() objetivoVenda: boolean = null;
    /** Objetivo de venda incluso */
    @Input() objetivoVendaIn: boolean = null;
    /** Objetivo de bonificação */
    @Input() objetivoBonificacao: boolean = null;
    /** Objetivo de bonificação incluso */
    @Input() objetivoBonificacaoIn: boolean = null;
    /** Objetivo de aquisição */
    @Input() objetivoAquisicao: boolean = null;
    /** Objetivo de aquisição incluso */
    @Input() objetivoAquisicaoIn: boolean = null;

    /** Ao destruir o multiselect */
    @Output() onDestroy = new EventEmitter<void>();

    // Settings
    settings: IMultiSelectSettings;
    @Input() pullRight = false;
    @Input() enableSearch = true;
    @Input() showCheckAll = true;
    @Input() showUncheckAll = true;
    @Input() dynamicTitleMaxItems = 0;
    @Input() fixedTitle = false;
    /** Se deve exibir o multiselect acima */
    @Input() dropup = false;
    /** Se deve desabilitar o multiselect */
    @Input() disabled = false;

    // Texts
    texts: IMultiSelectTexts;
    @Input() checkAll = 'Selecionar Todas';
    @Input() uncheckAll = 'Limpar';
    @Input() defaultTitle = 'Nenhuma campanha';
    @Input() checked = 'Campanha';
    @Input() checkedPlural = 'Campanhas';
    @Input() searchPlaceholder = 'Pesquisar...';
    @Input() allSelected = 'Todas Selecionadas';
    @Input() searchEmptyResult = 'Nenhuma campanha encontrada';
    @Input() searchNoRenderText = 'Digite para pesquisar';

    // Options
    options: IMultiSelectOption[];

    areasCampanhas: AreaCampanhaSelect[];

    constructor(private messageService: MessageService,
                private campanhaSearchHttpService: CampanhaSearchHttpService) { }

    ngOnInit() {
        // Settings
        this.settings = {
            pullRight: this.pullRight,
            enableSearch: this.enableSearch,
            containerClasses: `container-multiselect container-multiselect-campanha ${this.dropup ? 'dropup' : 'dropdown'}`,
            showCheckAll: this.showCheckAll,
            showUncheckAll: this.showUncheckAll,
            dynamicTitleMaxItems: this.dynamicTitleMaxItems,
            fixedTitle: this.fixedTitle
        };
        // Texts
        this.texts = {
            checkAll: this.checkAll,
            uncheckAll: this.uncheckAll,
            defaultTitle: this.defaultTitle,
            checked: this.checked,
            checkedPlural: this.checkedPlural,
            searchPlaceholder: this.searchPlaceholder,
            allSelected: this.allSelected,
            searchEmptyResult: this.searchEmptyResult,
            searchNoRenderText: this.searchNoRenderText
        };

        this.search();
    }

    toggleSomenteEmAndamento(value: boolean) {
        this.somenteEmAndamento = value;
        this.somenteEmAndamentoChange.emit(this.somenteEmAndamento);
        this.search();
    }

    /** Cria o filter para pesquisar as campanhas */
    private createFilter(): CampanhaFilter {
        const filter = new CampanhaFilter(null);

        // Somente em andamento
        if (this.somenteEmAndamento) {
            filter.statuses = [campanhaStatusEmAndamento.id];
        }

        // Somente em uso
        if (this.somenteEmUso != null && this.somenteEmUso !== undefined) {
            filter.somenteEmUso = this.somenteEmUso;
        }

        // Objetivo de venda
        if (this.objetivoVenda != null && this.objetivoVenda !== undefined) {
            filter.objetivoVenda = this.objetivoVenda;
        }
        if (this.objetivoVendaIn != null && this.objetivoVendaIn !== undefined) {
            filter.objetivoVendaIn = this.objetivoVendaIn;
        }

        // Objetivo de bonificação
        if (this.objetivoBonificacao != null && this.objetivoBonificacao !== undefined) {
            filter.objetivoBonificacao = this.objetivoBonificacao;
        }
        if (this.objetivoBonificacaoIn != null && this.objetivoBonificacaoIn !== undefined) {
            filter.objetivoBonificacaoIn = this.objetivoBonificacaoIn;
        }

        // Objetivo de aquisição
        if (this.objetivoAquisicao != null && this.objetivoAquisicao !== undefined) {
            filter.objetivoAquisicao = this.objetivoAquisicao;
        }
        if (this.objetivoAquisicaoIn != null && this.objetivoAquisicaoIn !== undefined) {
            filter.objetivoAquisicaoIn = this.objetivoAquisicaoIn;
        }

        return filter;
    }

    /** Cria as campanhas para o multiselect */
    private search() {
        this.areasCampanhas = [];
        this.options = [];
        const selects: IMultiSelectOption[] = [];

        const filter  = this.createFilter();
        this.campanhaSearchHttpService.searchSelect(filter, CampanhaSearchOrderBy.NomeArea, 'asc')
            .pipe(
                take(1)
            )
            .subscribe(
                campanhas => {
                    this.areasCampanhas = createAreasCampanhasSelect(campanhas);

                    // Adicionando as áreas e suas campanhas ao multiselect
                    if (this.areasCampanhas) {
                        // Adicionando a opção de "Sem Campanha"
                        if (this.showNullOption) {
                            selects.push({
                                id: 0,
                                name: 'SEM CAMPANHA'
                            });
                        }

                        // Áreas
                        this.areasCampanhas.forEach(area => {
                            // Área
                            selects.push({
                                id: area.id,
                                name: area.nome,
                                isLabel: true
                            });
                            // Campanhas
                            area.campanhas?.forEach(campanha => {
                                selects.push({
                                    id: campanha.id,
                                    name: campanha.nome
                                });
                            });
                        });
                        this.options = selects;
                    }
                },
                (error: ApiError) => {
                    this.messageService.error(error.message,  'Erro ao pesquisar as campanhas', error);
                }
            );
    }


    /** Ao alterar a seleção da opção campanha nula */
    private onChangeNullOption() {
        this.nullOption = this.idsCampanhas.includes(0);
        this.nullOptionChange.emit(this.nullOption);

    }

    /** Ao alterar a seleção das campanhas */
    onChange() {
        this.onChangeNullOption();
        this.idsCampanhasChange.emit(this.idsCampanhas);
    }

    ngOnDestroy() {
        this.onDestroy.emit();
    }
}
